// Discipline — what broad subject area a course belongs to.
//
// Decision (Session 19BO+ · DEBT-19BO+-LINE1-FAKE-QUICK-MODE):
//   Coarse-grained "broad field" enum (8 fields + escape hatch). The earlier
//   14-bucket taxonomy tried to enumerate every university department and
//   still missed the long tail (philosophy of religion? aviation? veterinary?).
//   A discipline taxonomy cannot be exhaustive — so instead of enumerating
//   finer buckets, we collapse to broad fields whose internal pedagogy is
//   similar, and let users specify their exact subject via customDiscipline.
//
//   Two signal layers:
//     1. `discipline` (this enum) → DISCIPLINE_NOTE coarse subject-area tone
//        injected into the system prompt. STEM gets latex_escape etc.
//     2. `customDiscipline` (free text, max 80 chars) → always injected as
//        "SUBJECT AREA (user-specified)" so the LLM knows the exact subject.
//        This is where granularity lives — "法学", "心理学", "有机化学".
//
//   Common quick-pick templates (法学 / 计算机 / 医学 ...) live in the web
//   layer (discipline-templates.ts) as presets of (discipline + customDiscipline);
//   they do NOT extend this enum.
//
//   Pedagogy (the 8 PedagogyType values) is the DOMINANT signal for component
//   defaults — discipline only adds a subject-area tone. See component-defaults.ts
//   header comment.
//
// Why these 8 fields (grouping logic):
//   - stem:        math/physics/chem/CS/engineering share LaTeX + derivation
//   - humanities:  literature/history/philosophy share text-analysis + essay
//   - social_science: psych/sociology/politics/education share empirical + theory
//   - business:    mgmt/marketing/finance/economics share frameworks + cases
//   - law:         separate from business — its tone (statutes/precedents/IRAC)
//                  is distinct enough to warrant prompt specialization later
//   - medicine:    clinical/pharma/nursing — case-based + heavy terminology
//   - languages:   foreign/classical — vocab/grammar/translation
//   - arts:        visual/performing/design — analysis + movements
//   - other:       escape hatch (rely on customDiscipline)
import { z } from 'zod';
export const DisciplineSchema = z.enum([
    'stem', // 数理工程 — 数学 / 物理 / 化学 / 计算机 / 工程
    'humanities', // 人文 — 文学 / 历史 / 哲学
    'social_science', // 社科 — 心理 / 社会 / 政治 / 教育
    'business', // 商科 — 管理 / 营销 / 财会 / 经济
    'law', // 法学 — 独立（未来 prompt 特化）
    'medicine', // 医学 — 临床 / 药学 / 护理
    'languages', // 语言 — 外语 / 古典语言
    'arts', // 艺术 — 视觉 / 表演 / 设计
    'other', // 其他（配 customDiscipline 文本）
]);
export const ALL_DISCIPLINES = DisciplineSchema.options;
/** Default discipline for new courses. 'other' lets the user fill in
 *  customDiscipline, but we still inject the broad-field NOTE when a real
 *  field is chosen. Form requires a non-empty pick before generate. */
export const DEFAULT_DISCIPLINE = 'other';
/**
 * zh/en display labels for each discipline. Used by the create-form picker.
 * Mirror of CONTENT_LANGUAGE_DESCRIBE in content-language.ts.
 */
export const DISCIPLINE_DESCRIBE = {
    stem: {
        zh: '数理工程',
        en: 'STEM',
        hint: { zh: '数学 / 物理 / 化学 / 计算机 / 工程', en: 'Math / Physics / Chemistry / CS / Engineering' },
    },
    humanities: {
        zh: '人文',
        en: 'Humanities',
        hint: { zh: '文学 / 历史 / 哲学', en: 'Literature / History / Philosophy' },
    },
    social_science: {
        zh: '社科',
        en: 'Social Science',
        hint: { zh: '心理 / 社会 / 政治 / 教育', en: 'Psychology / Sociology / Politics / Education' },
    },
    business: {
        zh: '商科',
        en: 'Business',
        hint: { zh: '管理 / 营销 / 财会 / 经济', en: 'Management / Marketing / Finance / Economics' },
    },
    law: {
        zh: '法学',
        en: 'Law',
        hint: { zh: '法理 / 宪法 / 民刑法 / 国际法', en: 'Jurisprudence / Constitutional / Civil-Criminal / International' },
    },
    medicine: {
        zh: '医学',
        en: 'Medicine',
        hint: { zh: '临床 / 药学 / 护理 / 公共卫生', en: 'Clinical / Pharmacy / Nursing / Public Health' },
    },
    languages: {
        zh: '语言',
        en: 'Languages',
        hint: { zh: '外语 / 古典语言', en: 'Foreign / Classical languages' },
    },
    arts: {
        zh: '艺术',
        en: 'Arts',
        hint: { zh: '视觉 / 表演 / 设计', en: 'Visual / Performing / Design' },
    },
    other: {
        zh: '其他',
        en: 'Other',
        hint: { zh: '选此项请在下方填写具体学科', en: 'Pick this and specify the subject below' },
    },
};
//# sourceMappingURL=discipline.js.map